import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildPriceSeriesColumns } from '../columns';
import {
  PRICE_SERIES_SORT_KEYS,
  type PriceSeries,
  type PriceSeriesSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<PriceSeries> | undefined;
  onEdit: (row: PriceSeries) => void;
  onSortChange: (sort: { sortBy?: PriceSeriesSortKey; sortDir?: SortDirection }) => void;
  canEdit: boolean;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(PRICE_SERIES_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: PriceSeriesSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as PriceSeriesSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function PriceSeriesGrid({ data, onEdit, onSortChange, canEdit }: Props) {
  const columnDefs = useMemo(
    () => buildPriceSeriesColumns({ onEdit }, canEdit),
    [onEdit, canEdit],
  );

  return (
    <div className="h-full w-full">
      <AgGridReact<PriceSeries>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={56}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
